/**
 * TipTapEditor — Éditeur rich text réutilisable pour les gazettes
 * Barre d'outils : Gras, Italique, Titres, Listes, Séparateur
 * Stocke le contenu en HTML
 */
import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3,
  Minus, Undo2, Redo2
} from 'lucide-react';

const MenuButton = ({ active, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active ? 'bg-primary/20 text-primary' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
    }`}
  >
    {children}
  </button>
);

export default function TipTapEditor({ content, onChange, placeholder, disabled }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Saisissez votre texte...',
      }),
    ],
    content: content || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (onChange) onChange(editor.getHTML());
    },
  });

  // Sync external content changes
  useEffect(() => {
    if (editor && content !== undefined && editor.getHTML() !== content) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  // Sync editable state
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  return (
    <div className={`border rounded-lg overflow-hidden ${disabled ? 'opacity-60' : 'focus-within:ring-1 focus-within:ring-primary focus-within:border-primary'}`}>
      {/* Toolbar */}
      {!disabled && (
        <div className="flex items-center gap-0.5 px-2 py-1 border-b bg-gray-50 flex-wrap">
          <MenuButton
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Gras"
          >
            <Bold size={15} />
          </MenuButton>
          <MenuButton
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Italique"
          >
            <Italic size={15} />
          </MenuButton>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <MenuButton
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title="Titre"
          >
            <Heading2 size={15} />
          </MenuButton>
          <MenuButton
            active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            title="Sous-titre"
          >
            <Heading3 size={15} />
          </MenuButton>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <MenuButton
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Liste à puces"
          >
            <List size={15} />
          </MenuButton>
          <MenuButton
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Liste numérotée"
          >
            <ListOrdered size={15} />
          </MenuButton>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <MenuButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Séparateur"
          >
            <Minus size={15} />
          </MenuButton>

          <div className="flex-1" />

          <MenuButton
            onClick={() => editor.chain().focus().undo().run()}
            title="Annuler"
          >
            <Undo2 size={15} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().redo().run()}
            title="Rétablir"
          >
            <Redo2 size={15} />
          </MenuButton>
        </div>
      )}

      {/* Editor content */}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-3 py-2 min-h-[80px] focus:outline-none
          [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[60px]
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-300
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
      />
    </div>
  );
}
